import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { ProjectService } from '../../../shared/services/project.service';
import { NotificationService } from '../../../shared/services/notification.service';
import { PhaseModel, Phase } from '../../../shared/models/project.model';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { ConfirmModalComponent } from '../../../shared/utils/modal-validation/modal-validation';
import { AddPhaseModelComponent } from '../add-phase-model/add-phase-model';
import { CdkDragDrop, CdkDrag, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';

@Component({
  selector: 'app-list-phase-model',
  templateUrl: './list-phase-model.html',
  styleUrls: ['./list-phase-model.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, ConfirmModalComponent, CdkDrag, CdkDropList, AddPhaseModelComponent],
})
export class ListPhaseModelComponent implements OnInit, OnDestroy {
  private projectService = inject(ProjectService);
  private notificationService = inject(NotificationService);

  phaseModels: PhaseModel[] = [];
  expandedModels: Set<string> = new Set();
  isLoading = true;

  // Add-template modal
  showAddModal = false;

  closeAddModal() {
    this.showAddModal = false;
    this.projectService.listAllPhaseList();
  }

  showConfirmDelete = false;
  modelIdToDelete: string | null = null;
  confirmMessage = '';

  private subscription?: Subscription;

  ngOnInit() {
    this.subscription = this.projectService.phaseModel$.subscribe(models => {
      this.phaseModels = models;
      this.isLoading = false;
    });
    this.projectService.listAllPhaseList();
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  toggleExpand(modelId: string) {
    if (this.expandedModels.has(modelId)) this.expandedModels.delete(modelId);
    else this.expandedModels.add(modelId);
  }

  isExpanded(modelId: string) {
    return this.expandedModels.has(modelId);
  }

  addPhase(model: PhaseModel) {
    model.phases.push({ name: '', color: '#000000', estimatedHours: 0 });
  }

  removePhase(model: PhaseModel, index: number) {
    model.phases.splice(index, 1);
  }

  async saveModel(model: PhaseModel) {
    if (!model.modelName.trim() || model.phases.some(p => !p.name.trim())) {
      this.notificationService.showToast('Please fill all the fiedl', 'error');
      return;
    }
    if (!model.id) return;
    try {
      await this.projectService.updatePhaseModel(model.id, model);
      this.notificationService.showToast('Successfuly synchronized !', 'success');
    } catch (err) {
      this.notificationService.showToast('Erro while saving', 'error');
    }
  }

  openDeleteConfirm(model: PhaseModel) {
    this.modelIdToDelete = model.id!;
    this.confirmMessage = `Are you sur that you want delete "${model.modelName}" and all his phases ?`;
    this.showConfirmDelete = true;
  }

  async handleConfirmDelete(result: 'ok' | 'cancel') {
    this.showConfirmDelete = false;
    if (result === 'ok' && this.modelIdToDelete) {
      this.isLoading = true;
      try {
        await this.projectService.deletePhaseModel(this.modelIdToDelete);
        this.notificationService.showToast('Model definitively deleted', 'success');
      } catch (err) {
        this.notificationService.showToast('Error while deleting', 'error');
      } finally {
        this.isLoading = false;
        this.modelIdToDelete = null;
      }
    }
  }

  // Drag & Drop handler for phases
  onPhaseDrop(event: CdkDragDrop<Phase[]>, model: PhaseModel) {
    if (event.previousIndex === event.currentIndex) return;

    moveItemInArray(model.phases, event.previousIndex, event.currentIndex);

    // Update order values
    model.phases.forEach((phase, index) => {
      phase.order = index;
    });

    // Auto-save the model with new order
    this.saveModel(model);
  }
}
